import { skipToken } from "@reduxjs/toolkit/query";
import { getInterviewSectionTitle } from "@repo/app-types/entities/interviewSection/definition";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { IconRefresh } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { getAxiosInstanceWithAuth } from "../../../../api/axiosConfig.js";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../../api/entities/interviewSectionDefinition.js";
import {
  InterviewSimulation,
  type ChatMessage,
} from "./interviewSimulation.js";

interface InterviewSimulationSlideoverProps {
  project: ProjectWithInterviewCount;
  isOpen: boolean;
  onClose: () => void;
}

export const InterviewSimulationSlideover: FC<
  InterviewSimulationSlideoverProps
> = ({ project, isOpen, onClose }) => {
  const [selectedSectionId, setSelectedSectionId] = useState<
    string | undefined
  >(undefined);
  const [resetCount, setResetCount] = useState(0);
  const isTrainingProject = getIsTrainingProject(project);
  const title = isTrainingProject
    ? "Preview this AI training"
    : "Preview this AI interview";
  const infoBannerTitle = isTrainingProject
    ? "Actual training sessions are conducted in a live voice conversation. Your responses won't be saved."
    : "Actual interviews are conducted in a live voice conversation. Your responses won't be saved.";
  const sectionLabel = isTrainingProject
    ? "Training section"
    : "Interview section";
  const loadingSectionsLabel = isTrainingProject
    ? "Loading training sections..."
    : "Loading interview sections...";

  const { data: fetchedSections, isLoading: isFetchingSections } =
    useAllInterviewSectionDefinitionsForJobTypeQuery(
      project.job_type?.id ? { jobTypeId: project.job_type.id } : skipToken,
    );

  // Format sections for dropdown
  const sectionOptions =
    fetchedSections?.map((section) => ({
      value: section.id,
      label: getInterviewSectionTitle(section.settings),
    })) || [];

  const interviewSectionDefinitionId =
    selectedSectionId ?? sectionOptions.at(0)?.value;

  const getNextMessage = async (
    messages: readonly ChatMessage[],
  ): Promise<{ messages: ChatMessage[] }> => {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.post(
      `/projects/${encodeURIComponent(project.id)}/simulate-conversation`,
      {
        messages,
        interviewSectionDefinitionId,
      },
    );
    return response.data;
  };

  const handleReset = () => {
    setResetCount((prev) => prev + 1);
  };

  const resetKey = `${project.id}-${interviewSectionDefinitionId}-${resetCount}`;

  return (
    <Slideover
      shouldShow={isOpen}
      onClose={onClose}
      title={title}
      size={Size.SMALL}
      buttons={
        <div className="flex space-x-4">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleReset}
            icon={<IconRefresh className="h-4 w-4" />}
          >
            Reset
          </Button>
          <Button onClick={onClose} variant={ButtonVariant.Primary}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex h-full flex-col">
        <div className="p-4">
          <InfoBanner title={infoBannerTitle} />
        </div>

        {/* Section Selector */}
        {project.job_type?.id && sectionOptions.length > 0 && (
          <div className="px-4">
            <div>
              <SimpleSelect
                label={sectionLabel}
                options={sectionOptions}
                value={selectedSectionId || sectionOptions[0]?.value || null}
                onChange={(value) => {
                  setSelectedSectionId(value);
                }}
                isDisabled={isFetchingSections}
              />
            </div>
            {isFetchingSections && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader style={LoaderStyle.ORBIT} />
                <span className="ml-2">{loadingSectionsLabel}</span>
              </div>
            )}
          </div>
        )}

        <InterviewSimulation
          getNextMessage={getNextMessage}
          resetKey={resetKey}
        />
      </div>
    </Slideover>
  );
};
